import React, { useCallback, useMemo, useState } from 'react';

import { useAppSelector, useTranslate } from '@src/core/hooks';
import { AccountStepCard, buyerSelector } from '@src/features/customers';
import {
  ExternalAuthenticationSetup,
  PaymentMethodBody,
  PaymentMethodList,
  PaymentSoftDescriptor,
  PixPaymentCard,
  PixReceiverDisclaimer,
  usePaymentOptions,
  usePixPayment,
  PIX_POLLING_INTERVAL,
  useProcessPayment,
  paymentInFlightSelector,
} from '@src/features/payments';
import { ExpiredTransactableAlert, PaymentErrorDialog, usePaymentErrorDialog } from '@src/features/payments/errors';
import { isTransactablePayableOrCompleted, resolveTransactableEntity } from '@src/features/transactables';
import { isPendingPix } from '@src/features/transactions';
import { directLinkStateSelector } from '@src/stores/directLink';
import { currentOrderSelector } from '@src/features/orders';
import { currentSaleSelector } from '@src/stores/sale';
import { currentSaleChangeSelector } from '@src/stores/saleChange';

import { useCheckoutMethodSelect, useCheckoutPaymentEvents } from './analytics';
import {
  PaymentOptionsCard,
  PaymentOptionsHeading,
  PaymentOptionsSubtitle,
  PaymentOptionsTitle,
  PaymentStepRoot,
} from './styles';
import { usePayWithPix } from './hooks/usePayWithPix';

export const CheckoutPaymentStep = () => {
  const { i18n } = useTranslate();

  const [buyer] = useAppSelector(buyerSelector);
  const order = useAppSelector(currentOrderSelector);
  const sale = useAppSelector(currentSaleSelector);
  const saleChange = useAppSelector(currentSaleChangeSelector);
  const { directLink } = useAppSelector(directLinkStateSelector);

  const transactable = useMemo(() => ({ order, sale, directLink, saleChange }), [order, sale, directLink, saleChange]);

  const { options, loading, selected, select } = usePaymentOptions(transactable);

  // Every pick the buyer makes goes through here, so it reaches analytics; the preselected method does not.
  const selectByBuyer = useCheckoutMethodSelect(selected, select);

  const [backToList, setBackToList] = useState(false);

  const backToTheList = useCallback(() => setBackToList(false), []);

  const { pay, syncStatus } = useProcessPayment(transactable);

  // `selected` is the method the buyer just tried, which is what tells the dialog whether pix is
  // still a way out — offering it to someone whose pix failed sends them back into the same error.
  const { dialog, dismiss } = usePaymentErrorDialog(options, selected);

  const expired = Boolean(resolveTransactableEntity(transactable)) && !isTransactablePayableOrCompleted(transactable);

  const payWithPix = usePayWithPix({
    dialog,
    dismiss,
    options,
    pay,
    select: selectByBuyer,
    onGenerated: backToTheList,
  });

  const lastTransaction = resolveTransactableEntity(transactable)?.lastTransaction;
  const showPixCard = isPendingPix(lastTransaction) && !backToList;
  const pixReceiver = lastTransaction?.pix?.receiverInfo;
  const chargeInFlight = useAppSelector(paymentInFlightSelector);

  usePixPayment({
    transaction: chargeInFlight ? undefined : lastTransaction,
    pollingIntervalMs: PIX_POLLING_INTERVAL,
    onSync: syncStatus,
  });

  useCheckoutPaymentEvents({ pixQrVisible: showPixCard, selectedMethod: selected?.paymentMethod.code });

  if (showPixCard) {
    return (
      <PaymentStepRoot data-testid='checkout-payment-step'>
        <PixPaymentCard transactable={transactable} onChangeMethod={() => setBackToList(true)} />

        {pixReceiver?.financialInstitution ? (
          <PixReceiverDisclaimer institution={pixReceiver.financialInstitution} />
        ) : null}
      </PaymentStepRoot>
    );
  }

  return (
    <PaymentStepRoot data-testid='checkout-payment-step'>
      {buyer?.email ? <AccountStepCard email={buyer.email} /> : null}

      <PaymentOptionsCard>
        <PaymentOptionsHeading>
          <PaymentOptionsTitle>{i18n('checkout_payment_step_title')}</PaymentOptionsTitle>
          <PaymentOptionsSubtitle>{i18n('checkout_payment_step_subtitle')}</PaymentOptionsSubtitle>
        </PaymentOptionsHeading>

        {expired ? (
          <ExpiredTransactableAlert transactable={transactable} />
        ) : (
          <PaymentMethodList
            options={options}
            loading={loading}
            selected={selected}
            onSelect={selectByBuyer}
            phoneVerifiedAt={buyer?.phoneVerifiedAt}
            directLinkType={directLink?.type}
            groupLabel={i18n('checkout_payment_step_subtitle')}
            renderBody={(option) => (
              <PaymentMethodBody option={option} transactable={transactable} onPaid={() => setBackToList(false)} />
            )}
          />
        )}

        <PaymentSoftDescriptor option={selected} />
      </PaymentOptionsCard>

      {selected ? <ExternalAuthenticationSetup paymentOption={selected} /> : null}

      {/* An expired transactable is the answer already: both dialog actions charge something that no
          longer accepts a charge, so the alert stands on its own. */}
      {dialog && !expired ? <PaymentErrorDialog copy={dialog} onRetry={dismiss} onPayWithPix={payWithPix} /> : null}
    </PaymentStepRoot>
  );
};

export default CheckoutPaymentStep;
